// Box-rig builder (MOB-1). Client-only (ENG-1). Turns a mob's registry `rig`
// description (part sizes/offsets/colours) into a Three.js group of coloured
// boxes; leg parts are re-pivoted at the hip so they can swing while walking.
import * as THREE from 'three';
import type { MobDef } from '../../../sim/registry/schemas';
import { ICON_SIZE, FACE_NAMES, iconToRgba, type IconData, type BlockTexture } from '../../../sim/registry/icon';

export interface RigInstance {
  group: THREE.Group;
  /** Leg meshes (pivoted at the hip) swung during the walk cycle. */
  legs: THREE.Mesh[];
  /** Head meshes rotated to track the look direction. */
  heads: THREE.Mesh[];
  materials: THREE.MeshLambertMaterial[];
  geometries: THREE.BoxGeometry[];
  /** Canvas textures built for painted parts (disposed with the rig). */
  textures: THREE.CanvasTexture[];
  /** Parts by rig name (sheep wool tint/hide, …). */
  byName: Map<string, THREE.Mesh>;
}

/** Build a fresh rig instance for a mob definition (one per live entity). */
export function buildRig(def: MobDef): RigInstance {
  const group = new THREE.Group();
  const legs: THREE.Mesh[] = [];
  const heads: THREE.Mesh[] = [];
  const materials: THREE.MeshLambertMaterial[] = [];
  const geometries: THREE.BoxGeometry[] = [];
  const textures: THREE.CanvasTexture[] = [];
  const byName = new Map<string, THREE.Mesh>();

  for (const part of def.rig) {
    const geo = new THREE.BoxGeometry(part.size[0], part.size[1], part.size[2]);
    // A painted part carries a skin per side; an unpainted one stays a flat
    // colour, which is what every mob shipped as.
    const skins = part.texture ? sideMaterials(part.texture, part.color, textures) : null;
    const mat = skins ?? new THREE.MeshLambertMaterial({ color: part.color });
    const mesh = new THREE.Mesh(geo, mat);
    for (const m of Array.isArray(mat) ? mat : [mat]) materials.push(m);
    geometries.push(geo);
    byName.set(part.name, mesh);

    if (part.leg) {
      // Re-pivot the leg to its top so a rotation swings it from the hip.
      geo.translate(0, -part.size[1] / 2, 0);
      mesh.position.set(part.pos[0], part.pos[1] + part.size[1] / 2, part.pos[2]);
      legs.push(mesh);
    } else {
      mesh.position.set(part.pos[0], part.pos[1], part.pos[2]);
    }
    if (part.head) heads.push(mesh);
    group.add(mesh);
  }
  return { group, legs, heads, materials, geometries, textures, byName };
}

/** Three's BoxGeometry expects six materials in +X, −X, +Y, −Y, +Z, −Z order,
 *  which is NOT the voxel face order — hence the explicit mapping. A side with
 *  no painted skin falls back to the part's flat colour. */
const BOX_SIDE_ORDER: readonly (typeof FACE_NAMES[number])[] = ['east', 'west', 'top', 'bottom', 'south', 'north'];

function sideMaterials(
  tex: BlockTexture, color: string, sink: THREE.CanvasTexture[],
): THREE.MeshLambertMaterial[] {
  return BOX_SIDE_ORDER.map((side) => {
    const icon = tex[side] ?? tex.all;
    if (!icon) return new THREE.MeshLambertMaterial({ color });
    const map = partTexture(icon);
    sink.push(map);
    return new THREE.MeshLambertMaterial({ map, color: 0xffffff });
  });
}

/** A nearest-filtered CanvasTexture from a painted part's palette+pixels. */
function partTexture(icon: IconData): THREE.CanvasTexture {
  const cvs = document.createElement('canvas');
  cvs.width = ICON_SIZE; cvs.height = ICON_SIZE;
  const ctx = cvs.getContext('2d')!;
  const img = ctx.createImageData(ICON_SIZE, ICON_SIZE);
  img.data.set(iconToRgba(icon));
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cvs);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Dispose a rig's GPU resources when its entity despawns. */
export function disposeRig(rig: RigInstance): void {
  for (const g of rig.geometries) g.dispose();
  for (const m of rig.materials) m.dispose();
  for (const t of rig.textures) t.dispose();
}
